/**
 * Where a country code sits on the globe, and which region it belongs to.
 *
 * The profile stores an ISO code; the globe needs a point to draw and the
 * breakdown needs a region to file it under. This is that table. Centroids
 * are rough by design -- a dot 200km off on a 160-pixel sphere is the same
 * pixel -- and the list is the countries players actually come from, not the
 * ISO registry. A code that is not here still shows up in the breakdown under
 * "Other"; it just has no dot.
 */
import type { Region } from './PlayerGlobe'

type Entry = [region: string, lat: number, lon: number, name: string]

export const REGION_NAME: Record<string, string> = {
    na: 'North America',
    sa: 'South America',
    eu: 'Europe',
    apac: 'Asia Pacific',
    mena: 'Middle East',
    af: 'Africa',
    oce: 'Oceania',
    other: 'Other',
}

const COUNTRIES: Record<string, Entry> = {
    // North America
    US: ['na', 39, -98, 'United States'],
    CA: ['na', 56, -106, 'Canada'],
    MX: ['na', 23, -102, 'Mexico'],
    GT: ['na', 15, -90, 'Guatemala'],
    CR: ['na', 10, -84, 'Costa Rica'],
    PA: ['na', 9, -80, 'Panama'],
    DO: ['na', 19, -70, 'Dominican Republic'],
    PR: ['na', 18, -66, 'Puerto Rico'],
    // South America
    BR: ['sa', -14, -51, 'Brazil'],
    AR: ['sa', -38, -63, 'Argentina'],
    CL: ['sa', -35, -71, 'Chile'],
    CO: ['sa', 4, -74, 'Colombia'],
    PE: ['sa', -9, -75, 'Peru'],
    VE: ['sa', 7, -66, 'Venezuela'],
    EC: ['sa', -2, -78, 'Ecuador'],
    UY: ['sa', -33, -56, 'Uruguay'],
    PY: ['sa', -23, -58, 'Paraguay'],
    BO: ['sa', -17, -64, 'Bolivia'],
    // Europe
    GB: ['eu', 54, -2, 'United Kingdom'],
    IE: ['eu', 53, -8, 'Ireland'],
    FR: ['eu', 46, 2, 'France'],
    DE: ['eu', 51, 10, 'Germany'],
    NL: ['eu', 52, 5, 'Netherlands'],
    BE: ['eu', 50, 4, 'Belgium'],
    ES: ['eu', 40, -4, 'Spain'],
    PT: ['eu', 39, -8, 'Portugal'],
    IT: ['eu', 42, 12, 'Italy'],
    CH: ['eu', 47, 8, 'Switzerland'],
    AT: ['eu', 47, 14, 'Austria'],
    PL: ['eu', 52, 19, 'Poland'],
    CZ: ['eu', 50, 15, 'Czechia'],
    SK: ['eu', 48, 19, 'Slovakia'],
    HU: ['eu', 47, 19, 'Hungary'],
    RO: ['eu', 46, 25, 'Romania'],
    BG: ['eu', 43, 25, 'Bulgaria'],
    GR: ['eu', 39, 22, 'Greece'],
    HR: ['eu', 45, 15, 'Croatia'],
    RS: ['eu', 44, 21, 'Serbia'],
    SI: ['eu', 46, 15, 'Slovenia'],
    UA: ['eu', 49, 32, 'Ukraine'],
    BY: ['eu', 53, 28, 'Belarus'],
    RU: ['eu', 56, 38, 'Russia'],
    LT: ['eu', 55, 24, 'Lithuania'],
    LV: ['eu', 57, 25, 'Latvia'],
    EE: ['eu', 59, 26, 'Estonia'],
    FI: ['eu', 62, 26, 'Finland'],
    SE: ['eu', 60, 15, 'Sweden'],
    NO: ['eu', 61, 9, 'Norway'],
    DK: ['eu', 56, 10, 'Denmark'],
    IS: ['eu', 65, -18, 'Iceland'],
    // Middle East
    TR: ['mena', 39, 35, 'Turkey'],
    AE: ['mena', 24, 54, 'UAE'],
    SA: ['mena', 24, 45, 'Saudi Arabia'],
    IL: ['mena', 31, 35, 'Israel'],
    QA: ['mena', 25, 51, 'Qatar'],
    KW: ['mena', 29, 48, 'Kuwait'],
    IR: ['mena', 32, 53, 'Iran'],
    IQ: ['mena', 33, 44, 'Iraq'],
    JO: ['mena', 31, 36, 'Jordan'],
    LB: ['mena', 34, 36, 'Lebanon'],
    EG: ['mena', 27, 30, 'Egypt'],
    MA: ['mena', 32, -7, 'Morocco'],
    DZ: ['mena', 28, 2, 'Algeria'],
    TN: ['mena', 34, 9, 'Tunisia'],
    // Africa
    ZA: ['af', -30, 25, 'South Africa'],
    NG: ['af', 9, 8, 'Nigeria'],
    KE: ['af', 0, 38, 'Kenya'],
    GH: ['af', 8, -1, 'Ghana'],
    ET: ['af', 9, 40, 'Ethiopia'],
    TZ: ['af', -6, 35, 'Tanzania'],
    // Asia Pacific
    PH: ['apac', 12, 122, 'Philippines'],
    ID: ['apac', -2, 118, 'Indonesia'],
    MY: ['apac', 4, 109, 'Malaysia'],
    SG: ['apac', 1, 104, 'Singapore'],
    TH: ['apac', 15, 101, 'Thailand'],
    VN: ['apac', 16, 108, 'Vietnam'],
    JP: ['apac', 36, 138, 'Japan'],
    KR: ['apac', 36, 128, 'South Korea'],
    CN: ['apac', 35, 105, 'China'],
    TW: ['apac', 24, 121, 'Taiwan'],
    HK: ['apac', 22, 114, 'Hong Kong'],
    IN: ['apac', 21, 78, 'India'],
    PK: ['apac', 30, 69, 'Pakistan'],
    BD: ['apac', 24, 90, 'Bangladesh'],
    LK: ['apac', 8, 81, 'Sri Lanka'],
    NP: ['apac', 28, 84, 'Nepal'],
    KZ: ['apac', 48, 68, 'Kazakhstan'],
    MN: ['apac', 47, 104, 'Mongolia'],
    // Oceania
    AU: ['oce', -25, 134, 'Australia'],
    NZ: ['oce', -41, 174, 'New Zealand'],
}

/** A flat list of country counts, as the API sends it, folded into the
 *  regions the globe draws. Anything unrecognised is kept, under "Other", so
 *  the totals still add up to the number of players. */
export const toRegions = (
    rows: { country_code: string | null; country_name: string; players: number }[],
): Region[] => {
    const byRegion = new Map<string, Region>()
    for (const row of rows) {
        const code = (row.country_code ?? '').toUpperCase()
        const known = COUNTRIES[code]
        const key = known ? known[0] : 'other'
        let region = byRegion.get(key)
        if (!region) {
            region = { key, name: REGION_NAME[key] ?? key, countries: [] }
            byRegion.set(key, region)
        }
        region.countries.push({
            code: code || '??',
            name: known ? known[3] : row.country_name || 'Unknown',
            players: row.players,
            // Off the globe entirely for a code we cannot place. The
            // projection hides anything at the pole tilted away from us.
            lat: known ? known[1] : -90,
            lon: known ? known[2] : 0,
        })
    }
    return [...byRegion.values()]
}

export const centroid = (code: string): { lat: number; lon: number } | null => {
    const known = COUNTRIES[code.toUpperCase()]
    return known ? { lat: known[1], lon: known[2] } : null
}
